import { defineStore } from 'pinia'

import { getDetailVendor, updateVendor } from '@/api/vendor-routes'
import { deleteInvitationForVendor, resendInvitationForVendor } from '@/api/invitations-routes'

import { useDefaultStore } from '@/store/defaultStore'
import { usePermissionsStore } from '@/store/permissionsStore'
import { useLocalFilterTableStore } from '@/store/localFilterTableStore'

import { SETTINGS_HEADER_TABLE } from '@/constants/headersTable/settingsHeaderTable'
import { FILTER_EMAIL, FILTER_INVITE_STATUS } from '@/constants/localFilters'
import { MEMBERS_HEADERS, MEMBERS_HEADERS_WITH_ACTIONS } from '@/constants/headersTable/membersHeadersTable'

export const useDetailVendorStore = defineStore('detailVendor', {
    state: () => ({
        slug: null,
        loading: false,
        updateLoading: false,
        actionLoading: false,
        isVendorDefault: false,
        formData: {
            name: null,
        },
        invitations: [],

        invitationsFilters: [
            FILTER_EMAIL,
            FILTER_INVITE_STATUS,
        ],

        permissions: {
            create_numbers: false,
            see_record: false,
            block_numbers: false,
            download_reports: false,
        },
        anotherFormData: {},
    }),

    getters: {
        headerByPermission() {
            const permissionsStore = usePermissionsStore()

            return permissionsStore.permissions.vendors.edit ? MEMBERS_HEADERS_WITH_ACTIONS : MEMBERS_HEADERS
        },
    },

    actions: {
        getQueryLocalStorage() {
            const localFilterTableStore = useLocalFilterTableStore()
            localFilterTableStore.initializeTableSettings(SETTINGS_HEADER_TABLE.vendorInvitations, this.invitationsFilters)
            localFilterTableStore.getQueryLocalStorage(SETTINGS_HEADER_TABLE.vendorInvitations, this.id)
        },

        setVendorSlug(slug) {
            this.slug = slug
        },

        addInvitations(newInvitations) {
            this.invitations.unshift(...newInvitations)
        },

        _changeInvitationAfterResend(slug) {
            const index = this.invitations.findIndex((i) => i.slug === slug)

            if (index + 1) {
                this.invitations[index].accepted = null
            }
        },

        _changeInvitationsAfterDelete(slug) {
            this.invitations = this.invitations.filter((i) => i.slug !== slug)
        },

        updatePermissions(permissions) {
            this.permissions = permissions

            this.goToUpdateVendor({})
        },

        parsePayload(payload) {
            this.id = payload.id
            this.formData.name = payload.name
            this.isVendorDefault = payload.is_default

            this.invitations = payload.invitations.map((invite) => ({ ...invite, accepted: !!invite.accepted }))

            Object.keys(this.permissions).forEach((name) => {
                this.permissions[name] = payload[name]
            })
            this.anotherFormData.status_id = payload.status_id
        },

        async goToLoadDetailVendor() {
            const defaultStore = useDefaultStore()

            this.loading = true

            const { success, payload, message } = await getDetailVendor(this.slug)

            if (success) {
                this.parsePayload(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return success
        },

        async goToUpdateVendor(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const formDataForSend = {
                ...this.formData,
                ...this.anotherFormData,
                ...this.permissions,
                ...formDataForUpdate,
            }

            const { success, message } = await updateVendor({
                formData: formDataForSend,
                slug: this.slug,
            })

            if (success) {
                this.parsePayload({ ...formDataForSend, invitations: this.invitations })

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false
        },

        async goToResendInvitation({ slug }) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await resendInvitationForVendor(slug)

            if (success) {
                this._changeInvitationAfterResend(slug)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },

        async goToDeleteInvitation({ slug }) {
            const defaultStore = useDefaultStore()

            this.actionLoading = true

            const { success, message } = await deleteInvitationForVendor(slug)

            if (success) {
                this._changeInvitationsAfterDelete(slug)
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.actionLoading = false

            return success
        },
    },
})
